<template>
  <div class="vf-page py-3">
    <header class="vf-toolbar">
      <div class="vf-toolbar__title">
        <span class="vf-toolbar__accent" aria-hidden="true"></span>
        <div>
          <h1>{{ isEdit ? 'Edit Vault Entry' : 'New Vault Entry' }}</h1>
          <p v-if="isEdit">Revealing or modifying this entry is logged in the audit trail.</p>
          <p v-else>Encrypted at rest. Only super-admins can read or write to the vault.</p>
        </div>
      </div>
      <div class="vf-toolbar__actions">
        <button type="button" class="vf-btn vf-btn--ghost" @click="goBack" :disabled="isSaving">Back</button>
      </div>
    </header>

    <CAlert v-if="errorMessage" color="danger" show class="vf-alert">{{ errorMessage }}</CAlert>
    <CAlert v-if="successMessage" color="success" show class="vf-alert">{{ successMessage }}</CAlert>

    <div v-if="isLoading" class="vf-loading">Loading entry…</div>

    <template v-else>
      <section class="vf-section">
        <header class="vf-section__head">
          <h2><span class="vf-step-num">1</span> Identity</h2>
        </header>
        <div class="vf-section__body">
          <div class="vf-grid">
            <div class="vf-field vf-field--wide">
              <AppInput
                no-margin
                label="Name"
                required
                placeholder="e.g. Razorpay — Production"
                :value="form.name"
                :disabled="isSaving"
                @update:value="form.name = $event"
              />
            </div>
            <div class="vf-field">
              <label class="vf-label">Category</label>
              <AppDropdown
                v-model="form.category"
                :options="categoryOptions"
                :disabled="isSaving"
              />
            </div>
            <div class="vf-field">
              <AppInput
                no-margin
                label="Primary identifier (shown in list)"
                placeholder="username or email — never a secret"
                :value="form.hint"
                :disabled="isSaving"
                @update:value="form.hint = $event"
              />
            </div>
            <div class="vf-field vf-field--wide">
              <AppInput
                no-margin
                label="Login URL"
                placeholder="https://…"
                :value="form.url"
                :disabled="isSaving"
                @update:value="form.url = $event"
              />
            </div>
            <div class="vf-field vf-field--wide">
              <label class="vf-toggle">
                <input type="checkbox" v-model="form.pinned" :disabled="isSaving" />
                <span>Pin to the top of the vault list</span>
              </label>
            </div>
          </div>
        </div>
      </section>

      <section class="vf-section">
        <header class="vf-section__head">
          <h2><span class="vf-step-num">2</span> Credentials</h2>
          <div class="vf-section__head-actions">
            <button type="button" class="vf-btn vf-btn--ghost vf-btn--sm" @click="addField" :disabled="isSaving">+ Add field</button>
          </div>
        </header>
        <div class="vf-section__body">
          <p class="vf-helper">
            Add one row per credential — username, password, API key, webhook secret, recovery email,
            etc. Mark anything sensitive with the lock toggle so it stays masked by default.
          </p>
          <div v-if="!form.fields.length" class="vf-empty">No fields yet. Click <strong>+ Add field</strong> to start.</div>
          <div
            v-for="(field, idx) in form.fields"
            :key="idx"
            class="vf-field-row"
          >
            <div class="vf-field-row__label">
              <AppInput
                no-margin
                :value="field.label"
                placeholder="Label (e.g. Key ID, Password, Webhook secret)"
                :disabled="isSaving"
                @update:value="field.label = $event"
              />
            </div>
            <div class="vf-field-row__value">
              <div class="vf-secret-input">
                <input
                  :type="(field.is_secret && !field._reveal) ? 'password' : 'text'"
                  class="vf-input"
                  :value="field.value"
                  :disabled="isSaving"
                  placeholder="Value"
                  autocomplete="new-password"
                  spellcheck="false"
                  @input="field.value = $event.target.value"
                />
                <button
                  v-if="field.is_secret"
                  type="button"
                  class="vf-icon-btn"
                  :title="field._reveal ? 'Hide' : 'Show'"
                  @click="field._reveal = !field._reveal"
                >
                  <svg v-if="!field._reveal" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                    <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z"/>
                    <circle cx="12" cy="12" r="3"/>
                  </svg>
                  <svg v-else viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
                    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
                    <line x1="1" y1="1" x2="23" y2="23"/>
                  </svg>
                </button>
                <button
                  type="button"
                  class="vf-icon-btn"
                  title="Copy"
                  @click="copyValue(field.value)"
                >
                  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                  </svg>
                </button>
              </div>
            </div>
            <div class="vf-field-row__meta">
              <label class="vf-toggle vf-toggle--inline">
                <input type="checkbox" v-model="field.is_secret" :disabled="isSaving" />
                <span>Secret</span>
              </label>
              <button type="button" class="vf-action-btn vf-action-btn--delete" @click="removeField(idx)" :disabled="isSaving">
                <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                  <polyline points="3 6 5 6 21 6"/>
                  <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                </svg>
              </button>
            </div>
          </div>
        </div>
      </section>

      <section class="vf-section">
        <header class="vf-section__head">
          <h2><span class="vf-step-num">3</span> Notes (encrypted)</h2>
        </header>
        <div class="vf-section__body">
          <textarea
            v-model="form.notes"
            class="vf-textarea"
            rows="5"
            :disabled="isSaving"
            placeholder="Recovery codes, escalation contacts, gotchas, rotation policy…"
          ></textarea>
        </div>
      </section>

      <div class="vf-footer">
        <button
          type="button"
          class="vf-btn vf-btn--ghost"
          :disabled="isSaving"
          @click="goBack"
        >Cancel</button>
        <button
          type="button"
          class="vf-btn vf-btn--primary"
          :disabled="isSaving || !isFormValid"
          @click="submit"
        >{{ isSaving ? 'Saving…' : (isEdit ? 'Save Changes' : 'Create Entry') }}</button>
      </div>
    </template>
  </div>
</template>

<script>
import axios from 'axios'
import AppDropdown from '@/components/AppDropdown.vue'
import AppInput from '@/components/AppInput.vue'

const CATEGORY_LABELS = {
  payment_gateway: 'Payment Gateway',
  email:           'Email',
  hosting:         'Hosting',
  domain:          'Domain / DNS',
  database:        'Database',
  cloud:           'Cloud',
  api:             'API / Integration',
  shipping:        'Shipping',
  social:          'Social',
  ads:             'Ads',
  analytics:       'Analytics',
  sms:             'SMS / OTP',
  storage:         'Storage',
  internal:        'Internal Tool',
  other:           'Other',
}

function emptyField() {
  return { label: '', value: '', is_secret: true, _reveal: false }
}

export default {
  name: 'VaultEntryForm',
  components: { AppDropdown, AppInput },
  data() {
    return {
      isLoading: false,
      isSaving: false,
      errorMessage: '',
      successMessage: '',
      form: {
        name:     '',
        category: 'other',
        url:      '',
        hint:     '',
        notes:    '',
        pinned:   false,
        fields:   [emptyField()],
      },
    }
  },
  computed: {
    isEdit() {
      return Boolean(this.$route.params.id)
    },
    entryId() {
      return this.$route.params.id ? Number(this.$route.params.id) : null
    },
    categoryOptions() {
      return Object.keys(CATEGORY_LABELS).map((c) => ({ value: c, label: CATEGORY_LABELS[c] }))
    },
    isFormValid() {
      if (!this.form.name || !this.form.name.trim()) {
        return false
      }
      // At least one populated field row OR a populated notes block —
      // an entry with neither would be a useless artefact.
      const hasField = this.form.fields.some((f) => (f.label || '').trim() || (f.value || ''))
      const hasNotes = (this.form.notes || '').trim().length > 0
      return hasField || hasNotes
    },
  },
  methods: {
    addField() {
      this.form.fields.push(emptyField())
    },
    removeField(idx) {
      this.form.fields.splice(idx, 1)
    },
    copyValue(value) {
      if (!value) {
        return
      }
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(value).catch(() => {})
      }
    },
    goBack() {
      this.$router.push({ path: '/vault' })
    },
    loadEntry() {
      if (!this.isEdit) {
        return
      }
      const self = this
      this.isLoading = true
      this.errorMessage = ''
      axios.get(`${this.$apiAdress}/api/vault/${this.entryId}`)
        .then(function (response) {
          const data = response.data || {}
          self.form.name     = data.name || ''
          self.form.category = data.category || 'other'
          self.form.url      = data.url || ''
          self.form.hint     = data.hint || ''
          self.form.notes    = data.notes || ''
          self.form.pinned   = Boolean(data.pinned)
          const fields = Array.isArray(data.fields) ? data.fields : []
          self.form.fields = fields.length
            ? fields.map((f) => ({
              label: f.label || '',
              value: f.value || '',
              is_secret: f.is_secret !== false,
              _reveal: false,
            }))
            : [emptyField()]
        })
        .catch(function (error) {
          if (self.bounceIfLocked(error)) {
            return
          }
          self.errorMessage = self.extractError(error, 'Could not load entry.')
        })
        .finally(function () {
          self.isLoading = false
        })
    },
    // The pincode gate can expire mid-task. If a vault API call comes
    // back 423 from the form page, bounce back to /vault where the
    // pin overlay will prompt for re-unlock. Anything half-typed on
    // this form is discarded — better to lose draft text than to keep
    // partially-validated secrets in memory after a re-lock.
    bounceIfLocked(error) {
      if (error && error.response && error.response.status === 423) {
        this.$router.replace({ path: '/vault' })
        return true
      }
      return false
    },
    submit() {
      if (!this.isFormValid) {
        return
      }
      const payload = {
        name:     this.form.name.trim(),
        category: this.form.category || 'other',
        url:      this.form.url || null,
        hint:     this.form.hint || null,
        notes:    this.form.notes || null,
        pinned:   Boolean(this.form.pinned),
        fields:   this.form.fields
          .filter((f) => (f.label || '').trim() || (f.value || ''))
          .map((f) => ({
            label:     (f.label || '').trim(),
            value:     f.value || '',
            is_secret: Boolean(f.is_secret),
          })),
      }

      const self = this
      this.isSaving = true
      this.errorMessage = ''
      this.successMessage = ''

      const request = this.isEdit
        ? axios.put(`${this.$apiAdress}/api/vault/${this.entryId}`, payload)
        : axios.post(`${this.$apiAdress}/api/vault`, payload)

      request
        .then(function () {
          self.successMessage = self.isEdit ? 'Entry updated.' : 'Entry created.'
          setTimeout(() => {
            self.$router.push({ path: '/vault' })
          }, 600)
        })
        .catch(function (error) {
          if (self.bounceIfLocked(error)) {
            return
          }
          self.errorMessage = self.extractError(error, 'Could not save entry.')
        })
        .finally(function () {
          self.isSaving = false
        })
    },
    extractError(error, fallback) {
      const data = error && error.response && error.response.data
      if (data && data.message) {
        return data.message
      }
      if (data && data.error && typeof data.error === 'object') {
        const first = Object.values(data.error)[0]
        if (Array.isArray(first) && first.length) {
          return first[0]
        }
      }
      return fallback
    },
  },
  mounted() {
    this.loadEntry()
  },
}
</script>

<style scoped>
.vf-page {
  padding-left: 1rem;
  padding-right: 1rem;
}

.vf-toolbar {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  align-items: flex-start;
  margin-bottom: 1rem;
}

.vf-toolbar__title {
  display: flex;
  gap: 0.85rem;
  align-items: stretch;
}

.vf-toolbar__accent {
  width: 4px;
  border-radius: 2px;
  background: linear-gradient(180deg, #0e5b46 0%, #16a07a 100%);
}

.vf-toolbar__title h1 {
  font-size: 1.15rem;
  font-weight: 700;
  color: #1f2a44;
  margin: 0;
}

.vf-toolbar__title p {
  font-size: 0.78rem;
  color: #6b7280;
  margin: 0.2rem 0 0;
}

.vf-toolbar__actions {
  display: inline-flex;
  gap: 0.55rem;
}

.vf-alert {
  border-radius: 10px;
  font-size: 0.85rem;
}

.vf-loading {
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 12px;
  padding: 1.5rem;
  text-align: center;
  color: #6b7280;
}

.vf-section {
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 12px;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
  margin-bottom: 1rem;
  overflow: hidden;
}

.vf-section__head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.7rem 1rem;
  background: #fbfcfe;
  border-bottom: 1px solid #eef1f7;
}

.vf-section__head h2 {
  font-size: 0.95rem;
  font-weight: 700;
  color: #1f2a44;
  margin: 0;
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
}

.vf-step-num {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 999px;
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  color: #ffffff;
  font-size: 0.7rem;
  font-weight: 700;
}

.vf-section__body {
  padding: 0.95rem 1rem;
}

.vf-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;
}

.vf-field {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.vf-field--wide {
  grid-column: 1 / -1;
}

.vf-label {
  font-size: 0.78rem;
  font-weight: 600;
  color: #4f5560;
  margin-bottom: 0.3rem;
}

.vf-helper {
  font-size: 0.78rem;
  color: #6b7280;
  margin: 0 0 0.7rem;
}

.vf-empty {
  font-size: 0.85rem;
  color: #6b7280;
  background: #fbfcfe;
  border: 1px dashed #d8dde6;
  border-radius: 8px;
  padding: 0.85rem;
  text-align: center;
}

.vf-field-row {
  display: grid;
  grid-template-columns: minmax(180px, 1fr) minmax(0, 2fr) auto;
  gap: 0.6rem;
  align-items: flex-end;
  padding: 0.45rem 0;
  border-bottom: 1px dashed #eef1f7;
}

.vf-field-row:last-child {
  border-bottom: 0;
}

.vf-field-row__meta {
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  padding-bottom: 0.15rem;
}

.vf-secret-input {
  position: relative;
  display: flex;
  align-items: stretch;
  gap: 0.35rem;
}

.vf-input {
  flex: 1 1 auto;
  height: 36px;
  padding: 0 0.7rem;
  border-radius: 8px;
  border: 1px solid #d8dde6;
  background: #ffffff;
  font-size: 0.88rem;
  color: #1f2a44;
  outline: none;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.vf-input:focus {
  border-color: #16a07a;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.18);
}

.vf-icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 36px;
  border-radius: 8px;
  border: 1px solid #e5e9f2;
  background: #ffffff;
  color: #4f5560;
  cursor: pointer;
  transition: border-color 0.15s ease, color 0.15s ease, background 0.15s ease;
}

.vf-icon-btn:hover {
  border-color: #16a07a;
  color: #0e5b46;
  background: #f5fbf8;
}

.vf-icon-btn:focus { outline: none; }
.vf-icon-btn::-moz-focus-inner { border: 0; }
.vf-icon-btn:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22);
}

.vf-action-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  height: 28px;
  padding: 0 0.55rem;
  border-radius: 6px;
  border: 1px solid #e5e9f2;
  background: #ffffff;
  color: #1f2a44;
  font-size: 0.76rem;
  font-weight: 600;
  cursor: pointer;
  transition: border-color 0.15s ease, color 0.15s ease, background 0.15s ease;
}

.vf-action-btn--delete:hover:not(:disabled) {
  border-color: #ef4444;
  color: #b91c1c;
  background: #fef2f2;
}

.vf-action-btn:focus { outline: none; }
.vf-action-btn::-moz-focus-inner { border: 0; }
.vf-action-btn:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22);
}

.vf-toggle {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  font-size: 0.85rem;
  color: #1f2a44;
  cursor: pointer;
  margin: 0;
}

.vf-toggle--inline {
  font-size: 0.78rem;
  color: #4f5560;
}

.vf-textarea {
  width: 100%;
  border-radius: 8px;
  border: 1px solid #d8dde6;
  padding: 0.6rem 0.7rem;
  font-size: 0.88rem;
  color: #1f2a44;
  outline: none;
  font-family: inherit;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
  resize: vertical;
}

.vf-textarea:focus {
  border-color: #16a07a;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.18);
}

.vf-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.55rem;
  margin-top: 0.5rem;
}

.vf-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 36px;
  min-width: 96px;
  padding: 0 1rem;
  border-radius: 8px;
  font-family: inherit;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease, filter 0.15s ease, background 0.15s ease, color 0.15s ease, border-color 0.15s ease;
}

.vf-btn--sm {
  height: 30px;
  min-width: 0;
  padding: 0 0.7rem;
  font-size: 0.78rem;
}

.vf-btn:focus { outline: none; }
.vf-btn::-moz-focus-inner { border: 0; }
.vf-btn:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22);
}

.vf-btn:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.vf-btn--primary {
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  border: 0;
  color: #ffffff;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.18);
}

.vf-btn--primary:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 14px rgba(14, 91, 70, 0.28);
  filter: brightness(1.05);
}

.vf-btn--ghost {
  background: #ffffff;
  border: 1px solid #e5e9f2;
  color: #1f2a44;
}

.vf-btn--ghost:hover:not(:disabled) {
  background: #f8fafc;
  border-color: #d8dde6;
}

@media (max-width: 720px) {
  .vf-grid {
    grid-template-columns: 1fr;
  }
  .vf-field-row {
    grid-template-columns: 1fr;
  }
}
</style>
